import React, { useEffect, useRef, useState } from 'react';
import { bool, string, number, func, array } from 'prop-types';
import { ALERT_TYPE, ShowToastMessage } from '@laramie/ui';
import { useEntidad } from '../../hooks/useEntidad';

import './index.scss'

// normaliza para comparar sin distinguir mayúsculas ni acentos
const normalize = (text) => String(text ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const Combobox = (props) => {

    const [state, setState] = useState({
        open: false,
        query: null, // null = sin tipeo: el input muestra la selección actual
        highlight: 0
    });

    const wrapperRef = useRef(null);
    const listRef = useRef(null);

    // refs con la última versión de estado/commit para los handlers globales
    const stateRef = useRef(state);
    stateRef.current = state;
    const commitQueryRef = useRef(null);

    const [getListEntidad] = useEntidad({
        entidades: props.data ? [] : [props.entidad],
        onLoaded: (entidades, isSuccess, error) => {
            if (isSuccess) {
                setState(prevState => ({...prevState}));
            }
            else {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
            }
        },
        memo: (props.memo) ? {
            key: props.entidad,
            timeout: 0
        }: null
    });

    // cierra al clickear fuera del componente, resolviendo el texto tipeado
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                commitQueryRef.current();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // mantiene visible la opción resaltada al navegar con teclado
    useEffect(() => {
        if (state.open && listRef.current) {
            const item = listRef.current.children[state.highlight];
            if (item) item.scrollIntoView({ block: 'nearest' });
        }
    }, [state.open, state.highlight]);

    function getListProxy() {
        return (props.data ?? getListEntidad(props.entidad)).filter(props.filter);
    }
    function getRowProxy(id) {
        return (props.data ?? getListEntidad(props.entidad)).find(f => f.id === id);
    }

    const options = getListProxy().filter(row =>
        (state.query === null || state.query === '') ||
        normalize(props.onFormat(row)).includes(normalize(state.query))
    );

    const onSelected = (id, row) => {
        if (props.onChange) {
            props.onChange({
                target: {
                    name: props.name,
                    type: 'entidad',
                    value: id,
                    row: row
                }
            });
        }
        setState({ open: false, query: null, highlight: 0 });
    }

    // resuelve el texto tipeado sin selección explícita al salir del campo:
    // match exacto u opción única filtrada → la selecciona; ambiguo → revierte
    const commitQuery = () => {
        const { open, query } = stateRef.current;
        if (!open && query === null) return;
        if (query) {
            const matches = getListProxy().filter(row =>
                normalize(props.onFormat(row)).includes(normalize(query))
            );
            const match = matches.find(row => normalize(props.onFormat(row)) === normalize(query))
                ?? matches.find(row => normalize(row.nombre ?? row.descripcion ?? '') === normalize(query))
                ?? ((matches.length === 1) ? matches[0] : null);
            if (match) {
                onSelected(match.id, match);
                return;
            }
        }
        setState(prevState => ({...prevState, open: false, query: null}));
    }
    commitQueryRef.current = commitQuery;

    const openList = () => {
        const selectedIndex = getListProxy().findIndex(f => f.id === props.value);
        setState(prevState => ({
            ...prevState,
            open: true,
            highlight: (selectedIndex >= 0) ? selectedIndex : 0
        }));
    }

    const onKeyDown = (e) => {
        if (!state.open) {
            if (e.key === 'Enter' || e.key === 'ArrowDown') {
                e.preventDefault();
                openList();
            }
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setState(prevState => ({...prevState, highlight: Math.min(prevState.highlight + 1, options.length - 1)}));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setState(prevState => ({...prevState, highlight: Math.max(prevState.highlight - 1, 0)}));
        }
        else if (e.key === 'Enter') {
            e.preventDefault();
            const row = options[state.highlight];
            if (row) onSelected(row.id, row);
        }
        else if (e.key === 'Escape') {
            setState(prevState => ({...prevState, open: false, query: null}));
        }
        else if (e.key === 'Tab') {
            commitQuery();
        }
    }

    const displayValue = (state.query !== null) ? state.query : props.onFormat(getRowProxy(props.value) ?? null);

    return (
        <div className='combobox-wrapper' ref={wrapperRef}>

            <input
                id={props.name}
                name={props.name}
                type="text"
                role="combobox"
                aria-expanded={state.open}
                aria-controls={`${props.name}-combobox-list`}
                aria-autocomplete="list"
                autoComplete="off"
                placeholder={props.placeholder}
                className={`combobox-input ${props.className}`}
                value={displayValue}
                onClick={() => { if (!state.open) openList(); }}
                onFocus={() => { if (!state.open) openList(); }}
                onChange={({ target }) => {
                    setState(prevState => ({...prevState, open: true, query: target.value, highlight: 0}));
                }}
                onKeyDown={onKeyDown}
                disabled={props.disabled}
            />

            {!props.disabled && <span
                className="material-symbols-outlined icon-cruz"
                onClick={() => onSelected(0, null)}
            >close</span>}

            {state.open && (
                <ul
                    id={`${props.name}-combobox-list`}
                    className="combobox-list"
                    role="listbox"
                    ref={listRef}
                    style={{ '--combobox-visible-items': props.maxVisible }}
                >
                    {options.map((row, index) => (
                        <li
                            key={row.id}
                            role="option"
                            aria-selected={row.id === props.value}
                            className={'combobox-option'
                                + ((index === state.highlight) ? ' combobox-option--highlighted' : '')
                                + ((row.id === props.value) ? ' combobox-option--selected' : '')}
                            onClick={() => onSelected(row.id, row)}
                            onMouseEnter={() => setState(prevState => ({...prevState, highlight: index}))}
                        >
                            {props.onFormat(row)}
                        </li>
                    ))}
                    {options.length === 0 && (
                        <li className="combobox-option combobox-option--empty">Sin resultados</li>
                    )}
                </ul>
            )}
        </div>
    )
}

Combobox.propTypes = {
    entidad: string,
    name: string.isRequired,
    placeholder: string,
    className: string,
    value: number,
    onChange: func,
    onFormat: func,
    filter: func,
    data: array,
    disabled: bool,
    memo: bool,
    maxVisible: number
};

Combobox.defaultProps = {
    entidad: '',
    placeholder: '',
    className: '',
    value: null,
    onChange: null,
    onFormat: (row) => {
        if (!row || !row.id) return '';
        const descripcion = row.nombre ?? row.descripcion ?? '';
        return ((row.codigo ?? '') !== '') ? `${row.codigo} - ${descripcion}` : descripcion;
    },
    filter: (row) => { return true },
    data: null,
    disabled: false,
    memo: true,
    maxVisible: 7
};

export default Combobox;
